/**
 * Pure date helpers for dashboard KPI drill-down URL construction.
 *
 * Angular parity: these replicate `fnDate.addDay(-N, new Date(dateNow)).toISOString().slice(0, 10)`
 * from `Frontend/Angular/src/app/core/functions/dates.function.ts`. The Angular helper uses
 * `Date.prototype.setDate` with a relative offset; we use millisecond arithmetic instead so the
 * function stays a pure expression with no mutation — same calendar result for the .NET API, which
 * accepts `YYYY-MM-DD` for `DateTime?` query parameters.
 */

/**
 * Returns the ISO date string (`YYYY-MM-DD`) for today minus `days` calendar days.
 *
 * `daysAgoIso(0)` === today's date.
 * `daysAgoIso(7)` === 7 days ago.
 * Negative `days` gives a future date (permitted, though unused in current drill-downs).
 *
 * The `.toISOString()` call produces UTC midnight; slicing to 10 chars gives a date-only string
 * that matches the .NET API's date-range parameter convention.
 */
export function daysAgoIso(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

/**
 * Convenience alias for `daysAgoIso(0)` — today's ISO date string (`YYYY-MM-DD`).
 */
export function todayIso(): string {
  return daysAgoIso(0);
}

/**
 * Returns the `YYYY-MM-DD` slice of an ISO date string for native
 * `<input type="date">` value binding. Returns `''` for null / empty
 * inputs so the input renders blank rather than `Invalid Date`.
 *
 * Lives here so the 4 list pages with edit dialogs that bind ISO date
 * strings to native date inputs stop redeclaring the same helper.
 */
export function isoDateOnly(value: string | null): string {
  if (value === null || value === '') return '';
  return value.length >= 10 ? value.slice(0, 10) : value;
}
